// src/components/ProductsShowcase.js
import React, { useEffect } from 'react';
import {
  Sparkles, MessageCircle, Camera, LayoutGrid, ArrowRight, ExternalLink,
  Mail, MessageSquare, Workflow, Bot, TrendingUp, Receipt
} from 'lucide-react';
import '../styles/ProductsShowcase.css';

const ProductsShowcase = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.Calendly) return;
    if (!document.querySelector('script[data-calendly-widget]')) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.setAttribute('data-calendly-widget', 'true');
      document.body.appendChild(script);
    }
  }, []);

  const handleBookConsult = () => {
    const calendlyUrl = 'https://calendly.com/edsytech-info/30min';
    if (window?.Calendly?.showPopupWidget) {
      try {
        window.Calendly.showPopupWidget(calendlyUrl);
      } catch (e) {
        window.open(calendlyUrl, '_blank', 'noopener');
      }
    } else {
      window.open(calendlyUrl, '_blank', 'noopener');
    }
  };

  return (
    <section id="products" className="products-section">
      <div className="products-container">
        <div className="section-header-center">
          <span className="products-badge">
            <Sparkles className="badge-icon" />
            AI Product Suite
          </span>
          <h2 className="products-title">
            AI Built Into Real Businesses — Not Just a Demo
          </h2>
          <p className="products-description">
            We don't just talk about AI — we ship it. Snippy and Sesslink are live products
            using AI to solve everyday problems for retailers and growing businesses.
          </p>
        </div>

        {/* Snippy */}
        <div className="product-block">
          <div className="product-visual product-visual-snippy">
            <div className="visual-card">
              <div className="visual-card-header">
                <span className="visual-dot" />
                <span className="visual-dot" />
                <span className="visual-dot" />
                <span className="visual-card-title">Snippy</span>
              </div>
              <div className="visual-modes">
                <div className="visual-mode">
                  <MessageCircle className="visual-mode-icon" />
                  <span>Chat</span>
                </div>
                <div className="visual-mode">
                  <Camera className="visual-mode-icon" />
                  <span>Photo</span>
                </div>
                <div className="visual-mode">
                  <LayoutGrid className="visual-mode-icon" />
                  <span>Catalogue</span>
                </div>
              </div>
              <div className="visual-chat-bubble">
                "Order me 2kg atta and a bottle of oil from the shop nearby"
              </div>
            </div>
          </div>

          <div className="product-copy">
            <span className="product-eyebrow product-eyebrow-snippy">AI Commerce</span>
            <h3 className="product-name">Snippy</h3>
            <p className="product-tagline">Order from nearby shops via chat, photo, or catalogue.</p>
            <p className="product-description">
              Snippy gives local retailers an AI-powered digital storefront. Customers order the
              way that's easiest for them — describe what they need in a chat, snap a photo of a
              product, or browse a catalogue — and Snippy connects them to shops nearby for fast,
              local fulfillment.
            </p>
            <ul className="product-features">
              <li>Conversational AI ordering — no app tutorial needed</li>
              <li>Photo-based product recognition for snap-and-shop ordering</li>
              <li>Digital catalogue for traditional browsing</li>
              <li>Proximity-based fulfillment from local shops</li>
              <li>Helps small retailers compete with large e-commerce platforms</li>
            </ul>
            <div className="product-tags">
              <span className="product-tag">Local Retail</span>
              <span className="product-tag">Conversational Commerce</span>
              <span className="product-tag">Computer Vision</span>
              <span className="product-tag">Same-Day Fulfillment</span>
            </div>
            <a
              href="https://snippy.live/"
              target="_blank"
              rel="noopener noreferrer"
              className="product-cta product-cta-snippy"
            >
              Visit Snippy
              <ExternalLink className="product-cta-icon" />
            </a>
          </div>
        </div>

        {/* Sesslink */}
        <div className="product-block product-block-reverse">
          <div className="product-copy">
            <span className="product-eyebrow product-eyebrow-sesslink">AI Business OS</span>
            <h3 className="product-name">Sesslink</h3>
            <p className="product-tagline">CRM, campaigns, WhatsApp, automation & 28 AI tools — one login.</p>
            <p className="product-description">
              Sesslink unifies CRM, email/WhatsApp/SMS campaigns, visual workflow automation, and
              a full ERP — powered by an AI Copilot with 28 tools that write, optimise, predict,
              and analyse across the account. Every message, task, and invoice lives against the
              right customer record.
            </p>
            <ul className="product-features">
              <li>AI Copilot writes emails, subject lines, WhatsApp templates & drip journeys</li>
              <li>AI lead scoring and deal win-probability on every pipeline stage</li>
              <li>30/60/90-day revenue forecasting & churn-risk prediction</li>
              <li>Official WhatsApp Business API with a live two-way inbox</li>
              <li>Drag-and-drop automation across email, WhatsApp, and SMS</li>
              <li>Built-in ERP — business profiles, client management & invoicing</li>
            </ul>
            <div className="product-tags">
              <span className="product-tag">CRM</span>
              <span className="product-tag">Marketing Automation</span>
              <span className="product-tag">WhatsApp Business API</span>
              <span className="product-tag">Sales Forecasting</span>
            </div>
            <a
              href="https://www.sesslink.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="product-cta product-cta-sesslink"
            >
              Explore Sesslink
              <ExternalLink className="product-cta-icon" />
            </a>
          </div>

          <div className="product-visual product-visual-sesslink">
            <div className="visual-card">
              <div className="visual-card-header">
                <span className="visual-dot" />
                <span className="visual-dot" />
                <span className="visual-dot" />
                <span className="visual-card-title">AI Copilot</span>
              </div>
              <div className="visual-grid">
                <div className="visual-grid-item">
                  <Mail className="visual-grid-icon" />
                  <span>Campaigns</span>
                </div>
                <div className="visual-grid-item">
                  <MessageSquare className="visual-grid-icon" />
                  <span>WhatsApp</span>
                </div>
                <div className="visual-grid-item">
                  <Workflow className="visual-grid-icon" />
                  <span>Automation</span>
                </div>
                <div className="visual-grid-item">
                  <Bot className="visual-grid-icon" />
                  <span>AI Copilot</span>
                </div>
                <div className="visual-grid-item">
                  <TrendingUp className="visual-grid-icon" />
                  <span>Forecasts</span>
                </div>
                <div className="visual-grid-item">
                  <Receipt className="visual-grid-icon" />
                  <span>ERP</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="products-cta-banner">
          <h3 className="products-cta-title">Want AI like this built into your business?</h3>
          <p className="products-cta-text">
            We build the same kind of AI-driven platforms for our clients — from custom copilots
            to full automation pipelines.
          </p>
          <button className="products-cta-button" onClick={handleBookConsult}>
            Talk to Us
            <ArrowRight className="product-cta-icon" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProductsShowcase;
